import store from "../../store";
import Currencies from "../Currencies.js";
import moment from "moment";
// ─── Helpers y Utilidades de Recetas AYB ───

export const resolveId = (item) => (item ? item.id || item.Id || null : null);
export const cloneObj = (obj) => JSON.parse(JSON.stringify(obj));

// ─── Builders de Maps indexados ───
export function buildModulosMap(modulos = []) {
  const map = new Map();
  (modulos || []).forEach((m) => {
    if (!m) return;
    const mVal = m.value !== undefined ? m.value : (m.id !== undefined ? m.id : m);
    const mText = m.text || m.Nombre || m.nombre || mVal;
    map.set(mVal, mText);
    if (m.id) map.set(m.id, mText);
    if (m.value) map.set(m.value, mText);
  });
  return map;
}

export function buildOrigenesMap(origenes = []) {
  const map = new Map();
  (origenes || []).forEach((o) => {
    if (!o) return;
    const oVal = o.id !== undefined ? o.id : (o.value !== undefined ? o.value : o);
    const oText = o.Nombre || o.nombre || o.text || oVal;
    map.set(oVal, oText);
    if (o.id) map.set(o.id, oText);
    if (o.value) map.set(o.value, oText);
  });
  return map;
}

export function buildModificadoresMap(allMods = []) {
  const map = new Map();
  (allMods || []).forEach((m) => {
    const id = resolveId(m);
    if (id) map.set(id, m);
  });
  return map;
}

export function formatVaunDate(value) {
  if (!value) return "";
  const num = Number(value);
  const m = (!isNaN(num) && num > 0)
    ? (num < 1e11 ? moment.unix(num) : moment(num))
    : moment(value);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
}

// ─── Resolución de nombres ───
export function getDeptoName(deptoId, modulosMap, modulos = []) {
  if (!deptoId) return "";
  if (typeof modulosMap === "function") return modulosMap(deptoId);
  if (modulosMap && typeof modulosMap.has === "function" && modulosMap.has(deptoId)) {
    return modulosMap.get(deptoId);
  }
  if (modulosMap && typeof modulosMap === "object" && deptoId in modulosMap) {
    return modulosMap[deptoId];
  }
  const found = (modulos || []).find(
    (m) =>
      (m.value !== undefined && m.value === deptoId) ||
      (m.id !== undefined && m.id === deptoId)
  );
  return found ? found.text || found.Nombre || found.nombre || deptoId : deptoId;
}

export function getOrigenName(origenId, origenesMap, origenes = []) {
  if (!origenId) return "";
  if (typeof origenesMap === "function") return origenesMap(origenId);
  if (origenesMap && typeof origenesMap.has === "function" && origenesMap.has(origenId)) {
    return origenesMap.get(origenId);
  }
  if (origenesMap && typeof origenesMap === "object" && origenId in origenesMap) {
    return origenesMap[origenId];
  }
  const found = (origenes || []).find(
    (o) =>
      (o.id !== undefined && o.id === origenId) ||
      (o.value !== undefined && o.value === origenId)
  );
  return found ? found.Nombre || found.nombre || found.text || origenId : origenId;
}

export function getModificadorName(id, modificadoresMap) {
  if (!id) return "";
  if (typeof modificadoresMap === "function") {
    return modificadoresMap(id);
  }
  if (
    modificadoresMap &&
    typeof modificadoresMap.has === "function" &&
    modificadoresMap.has(id)
  ) {
    const mod = modificadoresMap.get(id);
    return (mod && (mod.Nombre || mod.nombre || mod.text)) || id;
  }
  if (modificadoresMap && typeof modificadoresMap === "object" && id in modificadoresMap) {
    const mod = modificadoresMap[id];
    return (mod && (mod.Nombre || mod.nombre || mod.text)) || id;
  }
  return id;
}

// ─── Departamentos/CCs de una receta ───
export function getRecipeDeptosRows(recipe, modulosMap, modulos = []) {
  if (!recipe) return [];
  if (recipe.CCs && typeof recipe.CCs === "object") {
    return Object.entries(recipe.CCs).map(([depto, origenes]) => ({
      depto,
      deptoName: getDeptoName(depto, modulosMap, modulos),
      origenes: Array.isArray(origenes) ? origenes : origenes ? [origenes] : [],
    }));
  }
  if (Array.isArray(recipe.Departamentos) && recipe.Departamentos.length > 0) {
    return recipe.Departamentos.map((d) => ({
      depto: d,
      deptoName: getDeptoName(d, modulosMap, modulos),
      origenes: [],
    }));
  }
  if (recipe.selectedDepto) {
    const origenes = Array.isArray(recipe.selectedOrigen)
      ? recipe.selectedOrigen
      : recipe.selectedOrigen ? [recipe.selectedOrigen] : [];
    return [{
      depto: recipe.selectedDepto,
      deptoName: getDeptoName(recipe.selectedDepto, modulosMap, modulos),
      origenes,
    }];
  }
  return [];
}

export function getTotalAlmacenesCount(recipe, modulosMap, modulos = []) {
  const rows = getRecipeDeptosRows(recipe, modulosMap, modulos);
  return rows.reduce(
    (sum, row) => sum + (Array.isArray(row.origenes) ? row.origenes.length : 0),
    0
  );
}

/**
 * ReplicadorRecetas: Clase de dominio para gestionar la replicación y edición masiva de recetas AYB.
 * Centraliza la lógica de inicialización, cálculos de precios masivos, correlativos y armado de payload
 */
export default class ReplicadorRecetas {
  /**
   * Obtiene la siguiente serie de códigos numéricos disponibles sin colisionar con productos existentes.
   */
  static getNextAvailableCodigos(count = 1) {
    const allProds = store.getters.getProductos || [];
    let maxNum = 0;
    if (Array.isArray(allProds)) {
      allProds.forEach((p) => {
        if (p && p.Codigo !== undefined && p.Codigo !== null) {
          const num = parseInt(String(p.Codigo).trim(), 10);
          if (!isNaN(num) && num > maxNum && num < 100000000) {
            maxNum = num;
          }
        }
      });
    }
    const codigos = [];
    for (let i = 1; i <= count; i++) {
      codigos.push(String(maxNum + i));
    }
    return codigos;
  }

  /**
   * Inicializa la lista de recetas clonando datos originales y preparando la estructura reactiva.
   * Soporta tanto el modo 'replicate' (crear copias con nuevos nombres y códigos)
   * como 'edit' (edición masiva de recetas existentes preservando su identidad).
   */
  static initializeItems(recetas = [], mode = "replicate") {
    if (!Array.isArray(recetas) || recetas.length === 0) return [];

    const isEdit = mode === "edit";
    const generatedCodigos = !isEdit
      ? this.getNextAvailableCodigos(recetas.length)
      : [];

    const prod = store.getters.getProductos || [];
    const prodMap = new Map();
    prod.forEach((p) => {
      const pid = resolveId(p);
      if (pid) prodMap.set(pid, p);
    });

    const defaultCurrency = Currencies.getCurrencyDefault
      ? Currencies.getCurrencyDefault()
      : "";

    return recetas.map((r, rIdx) => {
      // Extracción de Departamentos y CCs (almacenes múltiples)
      const ccsMap = {};
      if (r.CCs) {
        const ccsArr = Array.isArray(r.CCs) ? r.CCs : [r.CCs];
        ccsArr.forEach((cItem) => {
          if (cItem && typeof cItem === "object") {
            Object.entries(cItem).forEach(([dKey, oVal]) => {
              if (dKey && dKey !== "0") {
                const oArr = Array.isArray(oVal)
                  ? [...oVal]
                  : oVal
                  ? [oVal]
                  : [];
                ccsMap[dKey] = oArr;
              }
            });
          }
        });
      }
      if (Array.isArray(r.Departamentos)) {
        r.Departamentos.forEach((d) => {
          if (d && !ccsMap[d]) {
            ccsMap[d] = [];
          }
        });
      }

      const deptosList = Object.keys(ccsMap);
      const primaryDepto = deptosList[0] || null;
      const primaryOrigenes =
        primaryDepto && Array.isArray(ccsMap[primaryDepto])
          ? [...ccsMap[primaryDepto]]
          : [];

      const vaunAux = formatVaunDate(r.Vaun);

      const resolvedCatalogo = Array.isArray(r.Catalogo)
        ? r.Catalogo.filter((ins) => ins && resolveId(ins)).map((ins) => {
            const insId = resolveId(ins);
            let name = ins.Nombre || "";
            let um =
              ins.UM ||
              (ins.Extra && ins.Extra.AYB && ins.Extra.AYB.UM) ||
              "";
            if (prodMap.has(insId)) {
              const pMatch = prodMap.get(insId);
              if (!name) name = pMatch.Nombre || "";
              if (!um)
                um =
                  (pMatch.Extra &&
                    pMatch.Extra.AYB &&
                    pMatch.Extra.AYB.UM) ||
                  pMatch.UM ||
                  "";
            }
            if (!um) um = "";
            const mm =
              ins.Mm !== undefined
                ? ins.Mm
                : (ins.Extra && ins.Extra.AYB && ins.Extra.AYB.Mm) || 0;
            return {
              Id: insId,
              id: insId,
              Nombre: name,
              Tipo: ins.Tipo || "AYB-Insumo",
              Cantidad: Number(ins.Cantidad) || 1,
              Mm: Number(mm) || 0,
              UM: um,
              Moneda: ins.Moneda || r.Moneda || defaultCurrency,
              Catalogo: Array.isArray(ins.Catalogo)
                ? cloneObj(ins.Catalogo)
                : [],
              Extra: ins.Extra
                ? cloneObj(ins.Extra)
                : { AYB: { UM: um, Mm: Number(mm) || 0 } },
            };
          })
        : [];

      const resolvedModificadores = Array.isArray(r.Modificadores)
        ? r.Modificadores.filter((m) => m && resolveId(m)).map((m) => {
            const mId = resolveId(m);
            return {
              id: mId,
              Id: mId,
              orn: m.orn || null,
            };
          })
        : [];

      const targetId = resolveId(r);
      const targetNombre = isEdit
        ? r.Nombre || ""
        : (r.Nombre || "") + " (Copia)";
      const targetCodigo = isEdit
        ? r.Codigo
        : generatedCodigos[rIdx] || String(Date.now() + rIdx);

      return {
        rawOriginal: r,
        id: targetId,
        Id: targetId,
        originalId: targetId,
        originalNombre: r.Nombre,
        originalCodigo: r.Codigo,
        originalPrecio: Number(r.Precio) || 0,
        originalCCs: cloneObj(ccsMap),
        originalDepartamentos: [...deptosList],
        originalCatalogo: cloneObj(resolvedCatalogo),
        originalModificadores: cloneObj(resolvedModificadores),
        included: true,
        Nombre: targetNombre,
        Codigo: targetCodigo,
        Categoria: r.Categoria || null,
        Precio: Number(r.Precio) || 0,
        Moneda: r.Moneda || defaultCurrency,
        selectedDepto: primaryDepto,
        selectedOrigen: primaryOrigenes,
        Departamentos: deptosList,
        CCs: ccsMap,
        Catalogo: cloneObj(resolvedCatalogo),
        Modificadores: cloneObj(resolvedModificadores),
        Extra: r.Extra
          ? cloneObj(r.Extra)
          : { AYB: { UM: "", Rm: 1 } },
        opdv: !!r.opdv,
        oweb: !!r.oweb,
        ocmd: !!r.ocmd,
        tags: Array.isArray(r.tags) ? [...r.tags] : [],
        vaunAux: vaunAux,
        Vaun: r.Vaun || null,
        dscr: r.dscr || "",
        UM: (r.Extra && r.Extra.AYB && r.Extra.AYB.UM) || "",
      };
    });
  }

  // Opciones iniciales para la edición masiva (agrupadas por sección)
  static getDefaultBatchOptions() {
    return {
      name: {
        enabled: false,
        mode: "prefix_suffix", // "prefix_suffix" | "replace" | "keep_original"
        prefix: "",
        suffix: "",
        baseOriginal: true,
        findText: "",
        replaceText: "",
        caseSensitive: false,
      },
      price: {
        enabled: false,
        type: "percent", // "percent" | "amount" | "fixed"
        percentType: 1,
        percentValue: 10,
        amountType: 1,
        amountValue: 20,
        fixedValue: 100,
      },
      category: {
        enabled: true,
        targetId: null,
      },
      channels: {
        enabled: false,
        pdv: true,
        web: true,
        cmd: true,
      },
      depto: {
        enabled: false,
        preserveExisting: false,
      },
    };
  }

  // Modifica el nombre de la receta (prefijo/sufijo, buscar y reemplazar o restaurar original)
  static transformRecipeName(currentName, originalName, config = {}) {
    const baseName = (originalName || currentName || "").trim();

    switch (config.mode) {
      case "prefix_suffix": {
        const pRaw = config.prefix ? String(config.prefix).trim() : "";
        const sRaw = config.suffix ? String(config.suffix).trim() : "";

        // Espacio automático después del prefijo y antes del sufijo
        const p = pRaw ? `${pRaw} ` : "";
        const s = sRaw ? ` ${sRaw}` : "";

        return `${p}${baseName}${s}`.trim();
      }
      case "replace": {
        if (!config.findText) return baseName;
        const flags = config.caseSensitive ? "g" : "gi";
        const escapedFind = config.findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        return baseName.replace(new RegExp(escapedFind, flags), config.replaceText || "").trim();
      }
      case "keep_original":
        return (originalName || baseName).trim();
      default:
        return baseName;
    }
  }

  // Calcula el nuevo precio según porcentaje, monto fijo o precio directo
  static computeBatchPrice(item, priceConfig = {}) {
    const basePrice =
      item.originalPrecio !== undefined
        ? item.originalPrecio
        : parseFloat(item.rawOriginal && item.rawOriginal.Precio) ||
          parseFloat(item.Precio) ||
          0;

    let newPrice = basePrice;
    const mode = priceConfig.type || priceConfig.priceAdjustmentType || "percent";

    if (mode === "percent") {
      const factor =
        1 +
        (Number(priceConfig.percentType || 1) *
          Number(priceConfig.percentValue || 0)) /
          100;
      newPrice = basePrice * factor;
    } else if (mode === "amount") {
      const delta =
        Number(priceConfig.amountType || 1) *
        Number(priceConfig.amountValue || 0);
      newPrice = basePrice + delta;
    } else if (mode === "fixed") {
      newPrice = Number(
        priceConfig.fixedValue !== undefined
          ? priceConfig.fixedValue
          : priceConfig.fixedPriceValue || 0
      );
    }

    return Math.max(0, parseFloat(Number(newPrice).toFixed(2)));
  }

  // Asigna departamentos y almacenes comunes a una receta
  static applyBatchDeptos(item, commonCCs = {}, preserveExisting = false, setProp = null) {
    const commonEntries = Object.entries(commonCCs || {});
    if (commonEntries.length === 0) return;

    const setter = setProp || ((obj, key, val) => { obj[key] = val; });

    if (preserveExisting) {
      if (!item.CCs || typeof item.CCs !== "object") {
        setter(item, "CCs", {});
      }
      commonEntries.forEach(([dKey, dOrigenes]) => {
        setter(item.CCs, dKey, [...(dOrigenes || [])]);
      });
    } else {
      const clonedCCs = {};
      commonEntries.forEach(([dKey, dOrigenes]) => {
        clonedCCs[dKey] = [...(dOrigenes || [])];
      });
      setter(item, "CCs", clonedCCs);
    }

    item.Departamentos = Object.keys(item.CCs);
    const firstDepto = item.Departamentos[0] || null;
    item.selectedDepto = firstDepto;
    item.selectedOrigen =
      firstDepto && item.CCs[firstDepto]
        ? [...item.CCs[firstDepto]]
        : [];
  }

  // Aplica los ajustes en lote a todas las recetas seleccionadas
  static applyMixedRules(items = [], mixedOptions = {}, mixedCommonCCs = {}, vmSet = null) {
    const setProp = vmSet || ((obj, key, val) => { obj[key] = val; });

    // Lee las opciones agrupadas (o las planas si vienen de versiones anteriores)
    const nameCfg = mixedOptions.name || {
      enabled: mixedOptions.applyName,
      mode: mixedOptions.nameMode,
      prefix: mixedOptions.namePrefix,
      suffix: mixedOptions.nameSuffix,
      baseOriginal: mixedOptions.nameBaseOriginal,
      findText: mixedOptions.nameFindText,
      replaceText: mixedOptions.nameReplaceText,
      caseSensitive: mixedOptions.nameCaseSensitive,
    };

    const priceCfg = mixedOptions.price || {
      enabled: mixedOptions.applyPrice,
      type: mixedOptions.priceAdjustmentType,
      percentType: mixedOptions.percentType,
      percentValue: mixedOptions.percentValue,
      amountType: mixedOptions.amountType,
      amountValue: mixedOptions.amountValue,
      fixedValue: mixedOptions.fixedPriceValue,
    };

    const categoryCfg = mixedOptions.category || {
      enabled: mixedOptions.applyCategory,
      targetId: mixedOptions.targetCategory,
    };

    const channelsCfg = mixedOptions.channels || {
      enabled: mixedOptions.applyChannels,
      pdv: mixedOptions.channelPdv,
      web: mixedOptions.channelWeb,
      cmd: mixedOptions.channelCmd,
    };

    const deptoCfg = mixedOptions.depto || {
      enabled: mixedOptions.applyDepto,
      preserveExisting: mixedOptions.preserveExistingDeptos,
    };

    items.forEach((item) => {
      // 1. Categoría común
      if (categoryCfg.enabled && categoryCfg.targetId) {
        setProp(item, "Categoria", categoryCfg.targetId);
      }

      // 2. Departamentos y Almacenes comunes
      if (deptoCfg.enabled) {
        this.applyBatchDeptos(item, mixedCommonCCs, deptoCfg.preserveExisting, setProp);
      }

      // 3. Ajuste de precio
      if (priceCfg.enabled) {
        const newPrice = this.computeBatchPrice(item, priceCfg);
        setProp(item, "Precio", newPrice);
      }

      // 4. Canales de venta
      if (channelsCfg.enabled) {
        if (channelsCfg.pdv !== undefined) setProp(item, "opdv", !channelsCfg.pdv);
        if (channelsCfg.web !== undefined) setProp(item, "oweb", !channelsCfg.web);
        if (channelsCfg.cmd !== undefined) setProp(item, "ocmd", !channelsCfg.cmd);
      }

      // 5. Ajuste masivo de nombres
      if (nameCfg.enabled) {
        const newName = this.transformRecipeName(item.Nombre, item.originalNombre, nameCfg);
        if (newName) setProp(item, "Nombre", newName);
      }
    });
  }

  /**
   * Prepara y sanitiza exhaustivamente el payload para la Cloud Function de Firebase.
   * Admite mode: 'replicate' (crear nuevo producto) o 'edit' (actualizar producto existente).
   */
  static prepareProductPayload(item, mode = "replicate") {
    const isEdit = mode === "edit";
    const payload = cloneObj(item.rawOriginal || {});

    payload.Tipo = "AYB-Producto";
    payload.Nombre = (item.Nombre || "").trim();
    payload.Codigo =
      (item.Codigo ? String(item.Codigo).trim() : "") || String(Date.now());
    payload.Categoria = item.Categoria;

    // Sanitización rigurosa de Departamentos y Centros de Consumo (Almacenes múltiples)
    const rawCcs = item.CCs && typeof item.CCs === "object" ? item.CCs : {};
    const cleanCcs = {};
    Object.entries(rawCcs).forEach(([dKey, oVal]) => {
      if (dKey && dKey !== "0") {
        const origs = Array.isArray(oVal) ? oVal : oVal ? [oVal] : [];
        const cleanOrigs = origs
          .map((o) =>
            typeof o === "object" && o !== null
              ? resolveId(o) || o.id
              : String(o)
          )
          .filter(Boolean);
        cleanCcs[dKey] = cleanOrigs;
      }
    });

    const deptos = Object.keys(cleanCcs);
    if (deptos.length > 0) {
      payload.Departamentos = deptos;
      payload.CCs = [cleanCcs];
    } else if (item.selectedDepto) {
      const origs = Array.isArray(item.selectedOrigen)
        ? item.selectedOrigen
        : item.selectedOrigen
        ? [item.selectedOrigen]
        : [];
      const cleanOrigs = origs
        .map((o) =>
          typeof o === "object" && o !== null
            ? resolveId(o) || o.id
            : String(o)
        )
        .filter(Boolean);
      payload.Departamentos = [item.selectedDepto];
      payload.CCs = [
        {
          [item.selectedDepto]: cleanOrigs,
        },
      ];
    } else {
      payload.Departamentos = [];
      payload.CCs = null;
    }

    payload.Precio = Math.max(
      0,
      isNaN(parseFloat(item.Precio)) ? 0 : parseFloat(item.Precio)
    );
    payload.Orden =
      Number(
        item.Orden !== undefined && item.Orden !== null
          ? item.Orden
          : payload.Orden || 0
      ) || 0;
    payload.Moneda =
      item.Moneda ||
      (Currencies.getCurrencyDefault ? Currencies.getCurrencyDefault() : "");

    // Catálogo de insumos sanitizado
    payload.Catalogo = (item.Catalogo || [])
      .filter((ins) => ins && resolveId(ins))
      .map((ins) => {
        const insId = String(resolveId(ins)).trim();
        let qty = Number(ins.Cantidad) || 1;
        if (qty <= 0) qty = 1;

        let mm =
          ins.Mm !== undefined
            ? ins.Mm
            : ins.Extra && ins.Extra.AYB && ins.Extra.AYB.Mm;
        mm = Number(mm) || 0;
        if (mm < 0) mm = 0;
        if (mm > 100) mm = 100;

        const um = String(
          ins.UM || (ins.Extra && ins.Extra.AYB && ins.Extra.AYB.UM) || ""
        )
          .trim()
          .toUpperCase();
        const tipo = String(ins.Tipo || "AYB-Insumo").trim();
        const nombre = String(ins.Nombre || "").trim();

        return {
          Id: insId,
          id: insId,
          Nombre: nombre,
          Tipo: tipo,
          Cantidad: Number(qty.toFixed(4)),
          Mm: Number(mm.toFixed(2)),
          UM: um,
          Moneda: ins.Moneda || payload.Moneda || "",
          Catalogo: Array.isArray(ins.Catalogo)
            ? ins.Catalogo.filter((sub) => sub && resolveId(sub)).map(
                (sub) => ({
                  Id: resolveId(sub),
                  id: resolveId(sub),
                  Nombre: sub.Nombre || "",
                  Tipo: sub.Tipo || "AYB-Insumo",
                  Cantidad: Number(sub.Cantidad || 1),
                  Mm: Number(sub.Mm || 0),
                  UM: sub.UM || "",
                  Extra: sub.Extra || null,
                })
              )
            : [],
          Extra: {
            ...(ins.Extra || {}),
            AYB: {
              ...((ins.Extra && ins.Extra.AYB) || {}),
              UM: um,
              Mm: Number(mm.toFixed(2)),
              Rm: Number((ins.Extra && ins.Extra.AYB && ins.Extra.AYB.Rm) || 1),
            },
          },
        };
      });

    // Modificadores vinculados
    if (Array.isArray(item.Modificadores) && item.Modificadores.length > 0) {
      payload.Modificadores = item.Modificadores.filter(
        (m) => m && resolveId(m)
      ).map((m) => {
        const cleanMod = {
          id: String(resolveId(m)).trim(),
        };
        if (m.orn && typeof m.orn === "string" && m.orn.trim() !== "") {
          cleanMod.orn = m.orn.trim();
        } else {
          cleanMod.orn = null;
        }
        return cleanMod;
      });
      if (payload.Modificadores.length === 0) {
        payload.Modificadores = null;
      }
    } else {
      payload.Modificadores = null;
    }

    // Rendimiento / Extra
    if (!payload.Extra) payload.Extra = {};
    if (!payload.Extra.AYB) payload.Extra.AYB = {};
    payload.Extra.AYB.UM = String(
      item.UM || payload.Extra.AYB.UM || ""
    ).trim().toUpperCase();
    payload.Extra.AYB.Rm = Number(
      (item.Extra && item.Extra.AYB && item.Extra.AYB.Rm) ||
        payload.Extra.AYB.Rm ||
        1
    );

    // Canales y visibilidad
    payload.opdv = !!item.opdv;
    payload.oweb = !!item.oweb;
    payload.ocmd = !!item.ocmd;

    // Etiquetas / Tags normalizadas a strings
    const rawTags = Array.isArray(item.tags) ? item.tags : [];
    payload.tags = Array.from(
      new Set(
        rawTags
          .map((t) =>
            typeof t === "object" && t !== null
              ? t.text || t.value || ""
              : String(t).trim()
          )
          .filter((t) => t.length > 0)
      )
    );

    // Vigencia (Vaun)
    if (item.vaunAux) {
      const dateObj = new Date(item.vaunAux + "T00:00:00");
      payload.Vaun = !isNaN(dateObj.getTime()) ? dateObj.getTime() : null;
    } else if (item.Vaun && typeof item.Vaun === "number") {
      payload.Vaun = item.Vaun;
    } else {
      payload.Vaun = null;
    }
    payload.dscr = item.dscr || "";

    // Limpieza de campos calculados y helpers UI que no van a la base de datos
    delete payload.Disponible;
    delete payload.categoria_name;
    delete payload.Familia;
    delete payload.Costeo;
    delete payload.PrecioSort;
    delete payload.ZImp;
    delete payload.Zimp;
    delete payload.origenesName;
    delete payload.Available;
    delete payload.nombreOrigen;
    delete payload.existenciaPrecio;
    delete payload.existenciaData;
    delete payload.invData;
    delete payload.vaunAux;
    delete payload.selectedDepto;
    delete payload.selectedOrigen;
    delete payload.included;
    delete payload.rawOriginal;
    delete payload.originalId;
    delete payload.originalNombre;
    delete payload.originalCodigo;
    delete payload.originalPrecio;
    delete payload.originalCCs;
    delete payload.originalDepartamentos;
    delete payload.originalCatalogo;
    delete payload.originalModificadores;
    delete payload._status;
    delete payload._error;
    delete payload.key;
    delete payload._key;
    delete payload.Tenant;

    if (isEdit) {
      // En modo edición es fundamental preservar el ID
      const targetId = resolveId(item) || item.originalId || resolveId(payload);
      payload.id = targetId;
      payload.Id = targetId;
      if (item.imgn !== undefined) {
        payload.imgn = !!item.imgn;
      }
    } else {
      // En modo replicación se descartan los IDs previos para que Firebase genere uno nuevo
      delete payload.id;
      delete payload.Id;
      delete payload.FechaCreacion;
      delete payload.Dlat;
      payload.Dlat = null;
      payload.imgn = false;
    }

    return payload;
  }

  static getPriceDiffInfo(item) {
    if (!item) {
      return { hasChanged: false, diff: 0, percentDiff: 0, orig: 0, curr: 0 };
    }
    const orig = Number(item.originalPrecio);
    const curr = Number(item.Precio);

    if (Number.isNaN(orig) || Number.isNaN(curr)) {
      return { hasChanged: false, diff: 0, percentDiff: 0, orig: 0, curr: 0 };
    }

    const origCents = Math.round(orig * 100);
    const currCents = Math.round(curr * 100);
    const diffCents = currCents - origCents;

    const hasChanged = diffCents !== 0;
    const diff = diffCents / 100;
    const percentDiff =
      origCents === 0
        ? 0
        : Math.round((diffCents / origCents) * 1000) / 10;

    return {
      hasChanged,
      diff,
      percentDiff,
      orig,
      curr,
    };
  }

  static hasPriceChanged(item) {
    return this.getPriceDiffInfo(item).hasChanged;
  }

  static getPriceDiff(item) {
    return this.getPriceDiffInfo(item).diff;
  }

  static getPricePercentDiff(item) {
    return this.getPriceDiffInfo(item).percentDiff;
  }

  static hasInsumosChanged(item) {
    return hasInsumosChanged(item);
  }

  static hasModificadoresChanged(item) {
    return hasModificadoresChanged(item);
  }

  static getInsumosDiffList(item, productsMap) {
    return getInsumosDiffList(item, productsMap);
  }

  static getModificadoresDiffList(item, modificadoresMap, origenesMap, origenes) {
    return getModificadoresDiffList(item, modificadoresMap, origenesMap, origenes);
  }

  static getOriginalInsumos(item) {
    return getOriginalInsumos(item);
  }

  static getOriginalModificadores(item) {
    return getOriginalModificadores(item);
  }

  static getOriginalDeptosSummary(item, modulosMap, modulos, origenesMap, origenes) {
    return getOriginalDeptosSummary(item, modulosMap, modulos, origenesMap, origenes);
  }

  static getOriginalDeptosRows(item, modulosMap, modulos) {
    return getOriginalDeptosRows(item, modulosMap, modulos);
  }

  static formatVaunDate(value) {
    return formatVaunDate(value);
  }

  static getDiffConfig(status, type, themeTextClass) {
    return getDiffConfig(status, type, themeTextClass);
  }
}

// ─── Helpers para Insumos, Modificadores y Diffs ───

export const DIFF_CONFIGS = [
  {
    status: "added",
    icon: "mdi-plus",
    iconColor: "green darken-2",
    textClass: "green--text text--darken-3",
    cantClass: "green--text text--darken-3",
    insumoBg: "#ecfdf5",
    insumoBorder: "#a7f3d0",
    modBg: "#ecfdf5",
    modBorder: "#a7f3d0",
    modIconColor: "green darken-2",
    modTextClass: "green--text text--darken-3",
  },
  {
    status: "deleted",
    icon: "mdi-minus",
    iconColor: "red darken-2",
    textClass: "red--text text--darken-3",
    cantClass: "red--text text--darken-3",
    insumoBg: "#fef2f2",
    insumoBorder: "#fecaca",
    modBg: "#fef2f2",
    modBorder: "#fecaca",
    modIconColor: "red darken-2",
    modTextClass: "red--text text--darken-3",
  },
  {
    status: "modified",
    icon: "mdi-pencil",
    iconColor: "orange darken-3",
    textClass: "orange--text text--darken-4",
    cantClass: "orange--text text--darken-4",
    insumoBg: "#fffbeb",
    insumoBorder: "#fde68a",
    modBg: "#fffbeb",
    modBorder: "#fde68a",
    modIconColor: "orange darken-3",
    modTextClass: "orange--text text--darken-4",
  },
];

export function getDiffConfig(
  status,
  type = "insumo",
  themeTextClass = "primary--text"
) {
  const match = DIFF_CONFIGS.find((c) => c.status === status);
  if (match) {
    const bg = type === "insumo" ? match.insumoBg : match.modBg;
    const border = type === "insumo" ? match.insumoBorder : match.modBorder;
    return {
      icon: match.icon,
      iconColor: type === "insumo" ? match.iconColor : match.modIconColor,
      textClass: type === "insumo" ? match.textClass : match.modTextClass,
      cantClass: match.cantClass,
      rowStyle: `background-color: ${bg}; border: 1px solid ${border}; font-size: 11px; line-height: 1.25;`,
    };
  }
  return type === "insumo"
    ? {
        icon: "mdi-circle-small",
        iconColor: "grey lighten-1",
        textClass: "text--primary",
        cantClass: themeTextClass,
        rowStyle:
          "background-color: #f1f5f9; font-size: 11px; line-height: 1.25;",
      }
    : {
        icon: "mdi-circle-small",
        iconColor: "purple darken-1",
        textClass: "purple--text text--darken-4",
        cantClass: themeTextClass,
        rowStyle:
          "background-color: #f3e8ff; font-size: 11px; line-height: 1.25;",
      };
}

export function getInsumoDisplayName(ins, productsMap) {
  if (!ins) return "Insumo sin datos";
  if (typeof productsMap === "function") {
    return productsMap(ins);
  }
  if (ins.Nombre && String(ins.Nombre).trim() !== "") {
    return ins.Nombre;
  }
  const id = resolveId(ins);
  if (id && productsMap && typeof productsMap.has === "function" && productsMap.has(id)) {
    const prod = productsMap.get(id);
    return (prod && prod.Nombre) || `Insumo (${id})`;
  }
  if (id && productsMap && typeof productsMap === "object" && id in productsMap) {
    const prod = productsMap[id];
    return (prod && prod.Nombre) || `Insumo (${id})`;
  }
  return id ? `Insumo (${id})` : "Insumo sin nombre";
}

export function getInsumoUM(ins, productsMap) {
  if (!ins) return "PZA";
  if (typeof productsMap === "function") {
    return ins.UM || "PZA";
  }
  if (ins.UM && String(ins.UM).trim() !== "") return ins.UM;
  if (ins.Extra && ins.Extra.AYB && ins.Extra.AYB.UM) {
    return ins.Extra.AYB.UM;
  }
  const id = resolveId(ins);
  if (id && productsMap && typeof productsMap.has === "function" && productsMap.has(id)) {
    const prod = productsMap.get(id);
    return (prod && prod.Extra && prod.Extra.AYB && prod.Extra.AYB.UM) || "PZA";
  }
  if (id && productsMap && typeof productsMap === "object" && id in productsMap) {
    const prod = productsMap[id];
    return (prod && prod.Extra && prod.Extra.AYB && prod.Extra.AYB.UM) || "PZA";
  }
  return "PZA";
}

export function getPriceDiffInfo(item) {
  return ReplicadorRecetas.getPriceDiffInfo(item);
}

export function hasInsumosChanged(item) {
  if (!item) return false;
  const orig = Array.isArray(item.originalCatalogo) ? item.originalCatalogo : [];
  const curr = Array.isArray(item.Catalogo) ? item.Catalogo : [];
  if (orig.length !== curr.length) return true;

  for (let i = 0; i < curr.length; i++) {
    const c = curr[i];
    const o = orig.find(
      (x) => String(resolveId(x)) === String(resolveId(c))
    );
    if (!o) return true;
    if (Number(c.Cantidad) !== Number(o.Cantidad)) return true;
    if (Number(c.Mm || 0) !== Number(o.Mm || 0)) return true;
  }
  return false;
}

export function hasModificadoresChanged(item) {
  if (!item) return false;
  const orig = Array.isArray(item.originalModificadores)
    ? item.originalModificadores
    : [];
  const curr = Array.isArray(item.Modificadores) ? item.Modificadores : [];
  if (orig.length !== curr.length) return true;

  for (let i = 0; i < curr.length; i++) {
    const c = curr[i];
    const o = orig.find(
      (x) =>
        String(resolveId(x)) === String(resolveId(c)) &&
        String(x.orn || "") === String(c.orn || "")
    );
    if (!o) return true;
  }
  return false;
}

export function getInsumosDiffList(item, productsMapOrFn, umFn) {
  if (!item) return [];
  const orig = Array.isArray(item.originalCatalogo) ? item.originalCatalogo : [];
  const curr = Array.isArray(item.Catalogo) ? item.Catalogo : [];

  const getInsumoKey = (ins) => {
    const id = resolveId(ins);
    if (id) return String(id);
    return ins && ins.Nombre ? String(ins.Nombre).trim() : "";
  };

  const resolveDisplayName = typeof productsMapOrFn === "function"
    ? productsMapOrFn
    : (ins) => getInsumoDisplayName(ins, productsMapOrFn);

  const resolveUM = typeof umFn === "function"
    ? umFn
    : (ins) => getInsumoUM(ins, productsMapOrFn);

  const result = [];
  const matchedCurrIndexes = new Set();

  orig.forEach((o) => {
    const oKey = getInsumoKey(o);
    const currIdx = curr.findIndex(
      (c, idx) => !matchedCurrIndexes.has(idx) && getInsumoKey(c) === oKey
    );

    const oName = resolveDisplayName(o);
    const oUm = resolveUM(o);
    const oCant = o.Cantidad !== undefined ? Number(o.Cantidad) || 0 : 1;

    if (currIdx === -1) {
      result.push({
        status: "deleted",
        id: oKey,
        displayName: oName,
        um: oUm,
        cant: oCant,
        prevCant: null,
        raw: o,
      });
    } else {
      matchedCurrIndexes.add(currIdx);
      const c = curr[currIdx];
      const cName = resolveDisplayName(c);
      const cUm = resolveUM(c);
      const cCant = c.Cantidad !== undefined ? Number(c.Cantidad) || 0 : 1;
      const cantChanged = Math.abs(cCant - oCant) > 0.0001;
      const mmChanged = (Number(c.Mm) || 0) !== (Number(o.Mm) || 0);

      if (cantChanged || mmChanged) {
        result.push({
          status: "modified",
          id: oKey,
          displayName: cName,
          um: cUm,
          cant: cCant,
          prevCant: oCant,
          raw: c,
        });
      } else {
        result.push({
          status: "unchanged",
          id: oKey,
          displayName: cName,
          um: cUm,
          cant: cCant,
          prevCant: null,
          raw: c,
        });
      }
    }
  });

  curr.forEach((c, idx) => {
    if (!matchedCurrIndexes.has(idx)) {
      const cKey = getInsumoKey(c);
      const cName = resolveDisplayName(c);
      const cUm = resolveUM(c);
      const cCant = c.Cantidad !== undefined ? Number(c.Cantidad) || 0 : 1;
      result.push({
        status: "added",
        id: cKey,
        displayName: cName,
        um: cUm,
        cant: cCant,
        prevCant: null,
        raw: c,
      });
    }
  });

  return result;
}

export function getModificadoresDiffList(
  item,
  modificadoresMapOrFn,
  origenesMapOrFn,
  origenes = []
) {
  if (!item) return [];
  const orig = Array.isArray(item.originalModificadores)
    ? item.originalModificadores
    : [];
  const curr = Array.isArray(item.Modificadores) ? item.Modificadores : [];

  const getModKey = (m) => {
    const id = resolveId(m);
    return id ? String(id) : "";
  };

  const resolveModName = typeof modificadoresMapOrFn === "function"
    ? (m) => modificadoresMapOrFn(m.id || m.Id || resolveId(m))
    : (m) => getModificadorName(m.id || m.Id || resolveId(m), modificadoresMapOrFn);

  const resolveOrnName = typeof origenesMapOrFn === "function"
    ? (orn) => (orn ? origenesMapOrFn(orn) : "")
    : (orn) => (orn ? getOrigenName(orn, origenesMapOrFn, origenes) : "");

  const result = [];
  const matchedCurrIndexes = new Set();

  orig.forEach((o) => {
    const oKey = getModKey(o);
    const currIdx = curr.findIndex(
      (c, idx) => !matchedCurrIndexes.has(idx) && getModKey(c) === oKey
    );

    const oName = resolveModName(o);
    const oOrnName = resolveOrnName(o.orn);

    if (currIdx === -1) {
      result.push({
        status: "deleted",
        id: oKey,
        name: oName,
        orn: o.orn,
        ornName: oOrnName,
        prevOrnName: null,
        raw: o,
      });
    } else {
      matchedCurrIndexes.add(currIdx);
      const c = curr[currIdx];
      const cName = resolveModName(c);
      const cOrnName = resolveOrnName(c.orn);
      const ornChanged = String(c.orn || "") !== String(o.orn || "");

      if (ornChanged) {
        result.push({
          status: "modified",
          id: oKey,
          name: cName,
          orn: c.orn,
          ornName: cOrnName,
          prevOrnName: oOrnName,
          raw: c,
        });
      } else {
        result.push({
          status: "unchanged",
          id: oKey,
          name: cName,
          orn: c.orn,
          ornName: cOrnName,
          prevOrnName: null,
          raw: c,
        });
      }
    }
  });

  curr.forEach((c, idx) => {
    if (!matchedCurrIndexes.has(idx)) {
      const cKey = getModKey(c);
      const cName = resolveModName(c);
      const cOrnName = resolveOrnName(c.orn);
      result.push({
        status: "added",
        id: cKey,
        name: cName,
        orn: c.orn,
        ornName: cOrnName,
        prevOrnName: null,
        raw: c,
      });
    }
  });

  return result;
}

export function getOriginalDeptosRows(item, modulosMap, modulos = []) {
  if (!item) return [];
  const ccs =
    item.originalCCs && typeof item.originalCCs === "object" && Object.keys(item.originalCCs).length > 0
      ? item.originalCCs
      : item.rawOriginal && item.rawOriginal.CCs && typeof item.rawOriginal.CCs === "object"
      ? item.rawOriginal.CCs
      : null;

  if (ccs) {
    return Object.entries(ccs).map(([depto, origenes]) => ({
      depto,
      deptoName: getDeptoName(depto, modulosMap, modulos),
      origenes: Array.isArray(origenes)
        ? origenes
        : origenes
        ? [origenes]
        : [],
    }));
  }

  const deptos =
    Array.isArray(item.originalDepartamentos) && item.originalDepartamentos.length > 0
      ? item.originalDepartamentos
      : item.rawOriginal && Array.isArray(item.rawOriginal.Departamentos)
      ? item.rawOriginal.Departamentos
      : [];

  if (deptos.length > 0) {
    return deptos.map((d) => ({
      depto: d,
      deptoName: getDeptoName(d, modulosMap, modulos),
      origenes: [],
    }));
  }

  return [];
}

export function getOriginalInsumos(item) {
  if (!item) return [];
  return Array.isArray(item.originalCatalogo) ? item.originalCatalogo : [];
}

export function getOriginalModificadores(item) {
  if (!item) return [];
  return Array.isArray(item.originalModificadores)
    ? item.originalModificadores
    : [];
}

export function getOriginalDeptosSummary(
  item,
  modulosMap,
  modulos = [],
  origenesMap,
  origenes = []
) {
  const rows = getOriginalDeptosRows(item, modulosMap, modulos);
  if (rows.length === 0) return "Sin departamento";
  return rows
    .map((r) => {
      const oNames =
        r.origenes.length > 0
          ? r.origenes
              .map((o) => getOrigenName(o, origenesMap, origenes))
              .join(", ")
          : "Sin almacén";
      return `${r.deptoName} [${oNames}]`;
    })
    .join("; ");
}
